/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useState } from 'react';
import { createLock } from 'blb-api/loans';
import * as Sentry from '@sentry/browser';
import { BlendBridge } from 'blb-types/blendBridge';
import { setIdentifier } from '../Fees/utils/closingFeesUtils';
import { get } from 'lodash';
import { getConfig } from 'blb-utils/config';

interface LockParams {
  loanId: string;
  type: BlendBridge.API.LockType;
  userId: string;
}

const CONFLICT_STATUS_CODE = 409;

export default (initialValue: boolean) => {
  const [lockIsAvailable, setLockIsAvailable] = useState(initialValue);

  const releaseLockViaFetch = ({ loanId, userId, type }: LockParams) => {
    const { csrfToken } = getConfig();
    fetch(`/api/internal/loans/${loanId}/lock`, {
      body: JSON.stringify({ isLocked: false, lockType: type, userId }),
      headers: {
        'Content-Type': 'application/json',
        'x-csrf-token': csrfToken,
      },
      keepalive: true, // guarantees request completing
      method: 'POST',
    });
  };

  const grabLock = async ({ loanId, userId, type }: LockParams) => {
    try {
      await createLock(loanId, userId, type, true);
      setLockIsAvailable(true);
    } catch (err) {
      setLockIsAvailable(false);
      const statusCode = get(err, 'response.data.error.statusCode');
      if (statusCode === CONFLICT_STATUS_CODE) {
        setIdentifier('lock', err);
      } else {
        setIdentifier('generic', err);
      }
    }
  };

  const releaseLock = async ({ loanId, userId, type }: LockParams) => {
    try {
      await createLock(loanId, userId, type, false);
      setLockIsAvailable(false);
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  return [lockIsAvailable, grabLock, releaseLock, releaseLockViaFetch] as const;
};
